import { Dispatch } from "react";

import { Answer } from "~/components/database/detail/database-content/types";
import { revalidate } from "~/lib/client/revalidate";
import { AnswerTypeEnum, Sdk } from "~/lib/graphql/client";
import { CreateFormEvent } from "~/lib/server/revalidator";

export type Category = {
  id: number;
  name: string;
};

export type Props = {
  categories: Category[];
};

export type Question = {
  content: string;
  answerTypeEnum: AnswerTypeEnum;
  position: number;
  title: string;
  description: string | null;
  label: string;
};

export type Form = {
  title: string;
  description: string;
  categoryIds: number[];
  questions: Question[];
};

// NOTE: answers をソートしたい場合に使用する
// ネストされていないプロパティ（answer 以外）を列挙することで柔軟に対応可能
export const PRESERVED_COLUMNS: { [key: string]: number } = {
  id: 0,
  user: 1,
  createdAt: 2,
} as const;

function validateForm(form: Form) {
  if (form.title.length === 0) {
    return false;
  }

  if (form.description.length === 0) {
    return false;
  }

  if (form.categoryIds.length === 0) {
    return false;
  }

  if (form.questions.length === 0) {
    return false;
  }

  if (
    form.questions.some(
      (question) => question.label.length === 0 || question.title.length === 0
    )
  ) {
    return false;
  }

  return true;
}

async function createForm(form: Form, client: Sdk) {
  if (!validateForm(form)) return;

  const { createForm } = await client.createForm({
    input: {
      form: {
        title: form.title,
        description: form.description,
        categoryIds: form.categoryIds,
        questions: form.questions,
      },
    },
  });

  if (!createForm || !createForm.form) return;

  // SSG
  const revalidateBody: CreateFormEvent = {
    event: "createForm",
    formId: createForm.form.id,
    categoryIds: form.categoryIds,
  };
  await revalidate(revalidateBody);
  return createForm.form;
}

function removeQuestion(
  questions: Question[],
  position: number,
  setQuestions: Dispatch<Question[]>
) {
  const newQuestions = questions
    .filter((question) => question.position !== position)
    .map((question, index) => {
      return { ...question, position: index };
    });
  return () => setQuestions([...newQuestions]);
}

function compareByAnswerType(answerA: Answer, answerB: Answer): number {
  switch (answerA.__typename) {
    case "TextAnswer":
      if (answerB.__typename !== "TextAnswer")
        throw new Error("invalid answer type");
      return answerA.textValue.localeCompare(answerB.textValue, "ja");
    case "ShortTextAnswer":
      if (answerB.__typename !== "ShortTextAnswer")
        throw new Error("invalid answer type");
      return answerA.shortTextValue.localeCompare(answerB.shortTextValue, "ja");
    case "LongTextAnswer":
      if (answerB.__typename !== "LongTextAnswer")
        throw new Error("invalid answer type");
      return answerA.longTextValue.localeCompare(answerB.longTextValue, "ja");
    case "DateAnswer":
      if (answerB.__typename !== "DateAnswer")
        throw new Error("invalid answer type");
      return answerA.dateValue - answerB.dateValue;
    case "LocationAnswer":
      if (answerB.__typename !== "LocationAnswer")
        throw new Error("invalid answer type");
      const { latitude: latitudeA } = answerA.locationValue;
      const { latitude: latitudeB } = answerB.locationValue;
      return latitudeA - latitudeB;
    case "NumberAnswer":
      if (answerB.__typename !== "NumberAnswer")
        throw new Error("invalid answer type");
      return answerA.numberValue - answerB.numberValue;
    case "ReferenceAnswer":
      if (answerB.__typename !== "ReferenceAnswer")
        throw new Error("invalid answer type");
      return answerA.referenceValue - answerB.referenceValue;
    case "UrlAnswer":
      if (answerB.__typename !== "UrlAnswer")
        throw new Error("invalid answer type");
      return answerA.urlValue.localeCompare(answerB.urlValue, "ja");
    case "ImageUrlAnswer":
      if (answerB.__typename !== "ImageUrlAnswer")
        throw new Error("invalid answer type");
      return answerA.imageUrlValue.localeCompare(answerB.imageUrlValue, "ja");
    default:
      throw new Error(
        `Unknown type: ${(answerA as { __typename: "__invalid__" }).__typename}`
      );
  }
}

const DEFAULT_DESCRIPTION = `【説明】
例：このデータベースは〇〇についての情報を集めるためのものです。

【想いや目的】
例：みなさんの〇〇に対する感想などを教えて下さい！
`;

export {
  validateForm,
  createForm,
  removeQuestion,
  compareByAnswerType,
  DEFAULT_DESCRIPTION,
};
